/**
 * useRefs — canonical dropdown lists (customers / engineers / approval
 * managers / project managers) from GET /api/refs, plus addRef() backing the
 * forms' "Add new…" option. Fetched per mount so a freshly-added entry shows
 * up the next time a form opens.
 *
 * On the GLOBAL KSA tab the main lists are never read or written: the
 * dropdowns offer the names already used on KSA records, and "Add new…" just
 * fills the field (the name is saved on the record itself).
 *
 * A name someone else adds while a form is open shows up in its dropdowns
 * too (r50 live events, kind 'refs'); a re-read that fails keeps the lists.
 *
 * removeRef() is the red x on a dropdown item (10/8/26): it takes the name
 * off that list (DELETE /api/refs) - records keep it. `mayRemove` says who
 * sees the x: whoever may change the lists (the server holds /api/refs to
 * Projects Edit), and never on the KSA tab, whose lists are its records.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { useDataScope } from '../context/DataScope';
import { latestReads, useLiveRefresh } from '../live/useLive';
import { useMyAccess } from './useMyAccess';
import { canDelete } from '../data/roles';

const EMPTY = { customers: [], engineers: [], approvalManagers: [], projectManagers: [] };

const KIND_LIST = {
  customer: 'customers',
  engineer: 'engineers',
  approval_manager: 'approvalManagers',
  project_manager: 'projectManagers',
};

/** The dropdown lists, built from KSA records plus names typed this session. */
export function refsFromRows(rows, added = []) {
  const sets = { customers: new Set(), engineers: new Set(), approvalManagers: new Set(), projectManagers: new Set() };
  for (const r of rows || []) {
    if (r.customer) sets.customers.add(r.customer);
    for (const e of String(r.engineer || '').split(',')) if (e.trim()) sets.engineers.add(e.trim());
    if (r.approvalManager) sets.approvalManagers.add(r.approvalManager);
    if (r.pm) sets.projectManagers.add(r.pm);
  }
  for (const [kind, name] of added) if (KIND_LIST[kind]) sets[KIND_LIST[kind]].add(name);
  const sorted = (s) => [...s].sort((a, b) => a.localeCompare(b));
  return {
    customers: sorted(sets.customers),
    engineers: sorted(sets.engineers).map((name) => ({ name })),
    approvalManagers: sorted(sets.approvalManagers),
    projectManagers: sorted(sets.projectManagers),
  };
}

export function useRefs() {
  const scope = useDataScope();
  const { allProjects } = useApp();
  const [refs, setRefs] = useState(EMPTY);
  const [added, setAdded] = useState([]); // KSA only: [kind, name] typed this session
  // a live re-read can still be out when "Add new…" re-reads: the older
  // answer landing last must not drop the name just added (latestReads)
  const [reads] = useState(latestReads);

  const reload = useCallback(async () => {
    if (scope) return;
    const t = reads.start();
    try {
      const r = await fetch('/api/refs');
      if (!r.ok) return;
      const body = await r.json();
      // Merge over EMPTY: a response missing a key must degrade to [], not
      // crash every consumer that maps over refs.engineers etc.
      if (reads.show(t)) setRefs({ ...EMPTY, ...body });
    } catch {
      /* offline/dev — dropdowns degrade to their current options */
    }
  }, [scope, reads]);

  useEffect(() => {
    reload();
  }, [reload]);
  // someone else's "Add new…" (the KSA tab's lists follow its own records)
  useLiveRefresh('refs', reload);

  // kind: 'customer' | 'engineer' | 'approval_manager' | 'project_manager'
  const addRef = useCallback(
    async (kind, name) => {
      const clean = (name || '').trim().replace(/\s+/g, ' ');
      if (!clean) return null;
      if (scope) {
        setAdded((a) => [...a, [kind, clean]]);
        return clean;
      }
      try {
        await fetch('/api/refs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ kind, name: clean }),
        });
        await reload();
      } catch {
        /* keep the typed value even if persist failed */
      }
      return clean;
    },
    [reload, scope]
  );

  // the red x: true when it went, false when the server refused or could not be reached
  const removeRef = useCallback(
    async (kind, name) => {
      if (scope || !name) return false;
      try {
        const r = await fetch('/api/refs', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ kind, name }),
        });
        if (!r.ok) return false;
        // gone from this form at once; the re-read confirms it
        const list = KIND_LIST[kind];
        if (list) {
          setRefs((cur) => ({
            ...cur,
            [list]: (cur[list] || []).filter((x) => (typeof x === 'string' ? x : x.name) !== name),
          }));
        }
        await reload();
        return true;
      } catch {
        return false;
      }
    },
    [reload, scope]
  );
  const access = useMyAccess();
  const mayRemove = !scope && canDelete(access, 'projects');

  const ksaRefs = useMemo(() => (scope ? refsFromRows(allProjects, added) : null), [scope, allProjects, added]);
  return { refs: ksaRefs || refs, addRef, removeRef, mayRemove };
}
